import { createMemo, For, type JSX, Show } from 'solid-js';
import { t } from '../../i18n';
import { useCardControls } from '../../lib/card-controls-context';
import { pressMove, useCardMove } from '../../lib/card-move';
import type { CardEpicRow, CardState } from '../../lib/machine-card';
import { epicGrippable, epicSubject } from '../../lib/move-items';
import { ControlNotices } from './ControlNotices';
import { EpicControls } from './EpicControls';
import { EpicModelPicker } from './EpicModelPicker';
import { MoveButton } from './MoveButton';
import { MoveGrip } from './MoveGrip';
import { MoveToMenu } from './MoveToMenu';

// One epic in Mission (#6785, prototype progressHTML, `:2353-2373`): its title and
// done/total, one segment per slice (done green, running live, the rest the track), and a
// meta line: its model (#6753, the picker for an admin, the chip otherwise; prototype
// modelPick `:2346`), its status word ("Paused by you" first when the epic's own
// `paused_at` is set) and, for an admin on this org's machine, Pause or Resume and Stop
// (epicCtl, `:2339`). Under it: the Stop confirm, the Stop note and answer, a refusal, and
// the slices a pause or resume skipped. Named by its title, never "Epic #<n>".
//
// MOVING IT (#6771, the epic grip `:2365`): for an admin on this org's machine, an epic whose
// jobs on this card wait on no person carries a grip and a Move button; a move names its plan.

export function EpicRow(props: {
  readonly epic: CardEpicRow;
  readonly state: CardState;
}): JSX.Element {
  const controls = useCardControls();
  const e = (): CardEpicRow => props.epic;
  const move = useCardMove();
  const subject = createMemo(() => {
    if (move === null || !move.on()) return null;
    const box = move.box();
    return epicGrippable(box, e().plan_id, move.byTask()) ? epicSubject(box, e(), t) : null;
  });
  const segments = (): ('done' | 'live' | 'todo')[] =>
    Array.from({ length: e().total }, (_, i) =>
      i < e().done ? 'done' : i < e().done + e().running ? 'live' : 'todo',
    );
  return (
    <div
      class="mission-epic"
      data-move-row={subject()?.key}
      onPointerDown={(ev) => {
        const s = subject();
        if (move !== null && s !== null) pressMove(move, s, ev);
      }}
    >
      <Show when={subject()}>{(s) => <MoveGrip subject={s()} />}</Show>
      <p class="mission-epic__head">
        <span class="mission-epic__title">
          <span class="mission-epic__word">{t('fleet.machines.card_epic')}</span> {e().title}
        </span>
        <span class="mission-epic__count">{e().count}</span>
      </p>
      <div
        class="mission-epic__bar"
        role="img"
        aria-label={
          e().running > 0
            ? t('fleet.machines.card_slices_running_aria', {
                done: e().done,
                total: e().total,
                running: e().running,
              })
            : t('fleet.machines.card_slices_aria', { done: e().done, total: e().total })
        }
      >
        <For each={segments()}>{(seg) => <i class={`mission-epic__seg--${seg}`} />}</For>
      </div>
      <div class="mission-epic__meta">
        <EpicModelPicker epic={e()} state={props.state} />
        <p class="mission-epic__status" data-tone={e().tone ?? undefined}>
          {e().status}
        </p>
        <EpicControls epic={e()} state={props.state} />
        <Show when={subject()}>{(s) => <MoveButton subject={s()} />}</Show>
      </div>
      <Show when={subject()}>{(s) => <MoveToMenu subject={s()} />}</Show>
      <Show when={controls}>
        {(h) => (
          <ControlNotices
            kind="epic"
            id={e().plan_id}
            name={e().title}
            skips={h().epics.skips(e().plan_id)}
            onConfirm={() => h().epics.confirmStop(e())}
          />
        )}
      </Show>
    </div>
  );
}
